import { useQuery } from '@apollo/client'
import { useLocalization } from '@faceup/localization'
import { Form } from '@faceup/ui'
import { Skeleton } from '@faceup/ui-base'
import { FormItemType, getTranslation, type Language } from '@faceup/utils'
import { type ReactNode, useEffect, useState } from 'react'
import { graphql } from '../../__generated__'
import type { Answer, Answers } from '../../hooks'
import type { PreviewCustomValues } from '../FollowUpPreview'
import { FormElement } from './components'

const query = graphql(`
  query CustomFormItemsQuery(
    $categoryId: CompanyReportCategoryGlobalId
    $includeDeletedOptions: Boolean
    $reportSourceId: ID!
  ) {
    formItems(categoryId: $categoryId, reportSourceId: $reportSourceId) {
      id
      formItemId
      type
      hasHint
      deletedAt
      isRequired(channelId: $reportSourceId)
      labelTranslations {
        language
        translation
      }
      hintTranslations {
        language
        translation
      }
      options(includeDeleted: $includeDeletedOptions) {
        order
        id
        deletedAt
        labelTranslations {
          translation
          language
        }
      }
      scaleStartLabelTranslations {
        language
        translation
      }
      scaleEndLabelTranslations {
        language
        translation
      }
    }
  }
`)

const excludedItemTypes = [
  FormItemType.Category,
  FormItemType.OrganizationalUnit,
  FormItemType.SenderName,
  FormItemType.Classroom,
]

type CustomFormItemsProps = {
  categoryId?: string
  reportSourceId: string
  values: Answers
  onChange: (key: string, answer: Answer) => void
  defaultLanguage?: Language
  moreInformationRender: ReactNode
  shouldIncludeDeletedOptions?: boolean
  overrideFormData?: PreviewCustomValues
}

export const CustomFormItems = (props: CustomFormItemsProps) => {
  const {
    categoryId,
    reportSourceId,
    values,
    onChange,
    defaultLanguage,
    moreInformationRender,
    shouldIncludeDeletedOptions = false,
    overrideFormData,
  } = props
  const [lastRefetchAt, setLastRefetchAt] = useState<number>()
  const { language } = useLocalization()

  const { data, loading, refetch } = useQuery(query, {
    fetchPolicy: 'network-only',
    variables: {
      categoryId,
      reportSourceId,
      includeDeletedOptions: shouldIncludeDeletedOptions,
    },
    skip: !categoryId,
  })

  useEffect(() => {
    if (overrideFormData?.fieldsReloadTimestamp !== lastRefetchAt) {
      setLastRefetchAt(overrideFormData?.fieldsReloadTimestamp)
      void refetch()
    }
  }, [lastRefetchAt, overrideFormData?.fieldsReloadTimestamp, refetch])

  if (loading) {
    return (
      <div className='flex flex-col gap-4 pb-0'>
        <Skeleton />
        <Skeleton />
      </div>
    )
  }

  return (
    <>
      {data?.formItems
        .filter(({ deletedAt, type }) => !deletedAt && !excludedItemTypes.includes(type))
        .map(item => {
          if (item.type === FormItemType.MoreInformation) {
            return moreInformationRender
          }

          const value = values[item.formItemId]
          return (
            <Form.Item
              key={item.formItemId}
              label={getTranslation(item.labelTranslations, language, defaultLanguage)}
              withAsterisk={item.isRequired}
              {...(value?.error && {
                errorMessage: value.error,
              })}
              hint={
                item.hasHint && item.hintTranslations
                  ? getTranslation(item.hintTranslations, language, defaultLanguage)
                  : undefined
              }
              ariaIds={{
                hintId:
                  item.hasHint && item.hintTranslations ? `${item.formItemId}-hint` : undefined,
                errorId: value?.error ? `${item.formItemId}-error` : undefined,
              }}
            >
              <FormElement
                item={item}
                type={item.type}
                values={value?.values ?? []}
                onChange={answer => onChange(item.formItemId, answer)}
                language={language}
                defaultLanguage={defaultLanguage}
                ariaIds={{
                  hintId:
                    item.hasHint && item.hintTranslations ? `${item.formItemId}-hint` : undefined,
                  errorId: value?.error ? `${item.formItemId}-error` : undefined,
                }}
              />
            </Form.Item>
          )
        })}
    </>
  )
}
